/**
 * CalendarGrid - Week view with chauffeur swimlanes
 */

import { useMemo, useState } from 'react';
import {
  format,
  startOfWeek,
  addDays,
  isSameDay,
  isToday,
} from 'date-fns';
import { nl } from 'date-fns/locale';
import { useCalendarStore } from '../../stores/calendar-store';
import { Avatar } from '../ui';
import { CreateRouteModal } from './CreateRouteModal';
import { RouteCardMenu } from './RouteCardMenu';
import { MergeRoutesModal } from './MergeRoutesModal';
import { CancelRouteModal } from './CancelRouteModal';
import { RescheduleRouteModal } from '../route-planner/RescheduleRouteModal';
import { RouteDuur } from './RouteDuur';
import { MetriekRij, metriekKleur, metriekPercentage, minutenMerk } from './RouteMetriek';
import { ChauffeurWisselDialog } from './ChauffeurWisselDialog';
import type { RouteListItem } from '../../lib/salesforce';
import { initials } from '../../lib/format';
import clsx from 'clsx';

// Icons
const LockIcon = () => (
  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
  </svg>
);

const PlusCircleIcon = ({ className = 'w-3.5 h-3.5' }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

// Compacte capaciteit voor de maandweergave: alleen de breuk, kleur bij knel.
// De weekkaart gebruikt MetriekRij (RouteMetriek.tsx) — één vorm voor ophalen,
// afleveren en dagduur.
function CapacityCompact({
  label,
  count,
  total,
}: {
  label: string;
  count: number;
  total: number | null | undefined;
}) {
  const kleur = metriekKleur(count, total, true);
  return (
    <span className="inline-flex items-baseline gap-0.5 text-[10px] tabular-nums text-gray-600">
      <span className="text-gray-400">{label}</span>
      <span className={clsx('font-semibold', kleur === 'over' ? 'text-red-700' : 'text-gray-800')}>{count}</span>
      {total != null && <span className="text-gray-400">/{total}</span>}
    </span>
  );
}

/** Chauffeurs die op één dag twee routes hebben (rood pilletje op de kaart). */
function dubbeleChauffeurs(routes: RouteListItem[]): Set<string> {
  const telling = new Map<string, number>();
  for (const r of routes) {
    if (!r.chauffeurId || r.status === 'Afgerond') continue;
    telling.set(r.chauffeurId, (telling.get(r.chauffeurId) ?? 0) + 1);
  }
  return new Set([...telling.entries()].filter(([, n]) => n > 1).map(([id]) => id));
}

/** Route zonder chauffeur binnen 3 dagen: amber in plaats van grijs. */
function chauffeurUrgent(datum: string): boolean {
  const dag = new Date(`${datum}T00:00:00`).getTime();
  const nu = new Date().setHours(0, 0, 0, 0);
  return dag - nu <= 3 * 86_400_000;
}

// Route card component - Stripe-style professional design
function RouteCard({
  route,
  onClick,
  onMergeClick,
  onAssignChauffeur,
  onCancelClick,
  onRescheduleClick,
  dubbel = false,
}: {
  route: RouteListItem;
  onClick: () => void;
  onMergeClick: (route: RouteListItem) => void;
  onAssignChauffeur?: (route: RouteListItem) => void;
  onCancelClick?: (route: RouteListItem) => void;
  onRescheduleClick?: (route: RouteListItem) => void;
  dubbel?: boolean;
}) {
  const displayName = route.routeName || route.name;
  const isCompleted = route.status === 'Afgerond';
  const isInProgress = route.status === 'Onderweg';


  // Border color: status, dan tijd (de dag van de chauffeur), dan stops-cap.
  // Rand: alleen de status van de dag. Het capaciteitssignaal zit in de balk.
  const getBorderColor = () => {
    if (isCompleted) return 'border-l-emerald-500';
    if (isInProgress) return 'border-l-amber-500';
    return 'border-l-gray-200';
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      className={clsx(
        'w-full text-left rounded-lg border border-gray-200 bg-white transition-all duration-200 cursor-pointer',
        'hover:shadow-md hover:border-gray-300 hover:-translate-y-0.5',
        'focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1',
        'border-l-4',
        getBorderColor(),
        isCompleted && 'opacity-60'
      )}
    >
      {/* Header */}
      <div className="px-3 pt-2.5 pb-1.5">
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-sm font-semibold text-gray-900 leading-tight truncate" title={displayName}>
            {displayName}
          </h3>
          <div className="flex items-center gap-1 flex-shrink-0">
            {route.locked && route.stopsAddedAfterLock > 0 && (
              <span className="inline-flex items-center gap-0.5 bg-amber-100 text-amber-700 text-[10px] font-bold px-1.5 py-0.5 rounded-full animate-pulse" title={`${route.stopsAddedAfterLock} stop(s) toegevoegd na vergrendeling`}>
                <PlusCircleIcon className="w-3 h-3" />
                +{route.stopsAddedAfterLock}
              </span>
            )}
            {route.locked && (
              <span className="text-gray-400">
                <LockIcon />
              </span>
            )}
            <RouteCardMenu
              route={route}
              onOpenInPlanner={onClick}
              onMergeClick={() => onMergeClick(route)}
              onAssignChauffeur={onAssignChauffeur ? () => onAssignChauffeur(route) : undefined}
              onCancelClick={onCancelClick ? () => onCancelClick(route) : undefined}
              onRescheduleClick={onRescheduleClick ? () => onRescheduleClick(route) : undefined}
            />
          </div>
        </div>

        {/* Chauffeur */}
        <div className="flex items-center gap-2 mt-1.5">
          {route.chauffeurName ? (
            <>
              <Avatar name={route.chauffeurName} size="sm" />
              <span className="text-xs text-gray-600 truncate">{route.chauffeurName}</span>
              {dubbel && !isCompleted && (
                <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-red-100 text-red-700" title="Deze chauffeur staat vandaag op meer dan één route">
                  dubbel
                </span>
              )}
            </>
          ) : (
            <span className={clsx('text-xs italic', !isCompleted && chauffeurUrgent(route.datum) ? 'text-amber-700 font-medium not-italic' : 'text-gray-400')}>
              Geen chauffeur
            </span>
          )}
          {route.bijrijderNodig && (
            <span
              className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${
                route.bijrijderName
                  ? 'bg-slate-100 text-slate-700'
                  : 'bg-amber-100 text-amber-800'
              }`}
              title={route.bijrijderName ? `Bijrijder: ${route.bijrijderName}` : 'Bijrijder nog niet toegewezen'}
            >
              <span aria-hidden>👥</span>
              {route.bijrijderName ? initials(route.bijrijderName) : 'TBD'}
            </span>
          )}
          <span className="ml-auto text-xs tabular-nums text-gray-500 whitespace-nowrap">
            <span className="font-semibold text-gray-800">{route.stopCount}</span> stops
          </span>
        </div>
      </div>

      {/* De drie beperkingen van de dag in één vorm: ophalen, afleveren, tijd. */}
      {!isCompleted && (
        <div className="px-3 py-2 border-t border-gray-100 space-y-1">
          <MetriekRij
            label="Ophalen"
            waarde={String(route.stopCountOphalen || 0)}
            totaal={route.capaciteitTotaalOphalen != null ? String(route.capaciteitTotaalOphalen) : null}
            pct={metriekPercentage(route.stopCountOphalen || 0, route.capaciteitTotaalOphalen)}
            kleur={metriekKleur(route.stopCountOphalen || 0, route.capaciteitTotaalOphalen, true)}
            overschreden={route.capaciteitResterendOphalen != null && route.capaciteitResterendOphalen < 0}
            title={route.ophalenNietVoorstellen ? 'Ophalen staat dicht voor nieuwe voorstellen' : minutenMerk(route.minutenStand?.ophalen)?.title}
            merk={route.ophalenNietVoorstellen ? <span className="text-[9px] text-gray-500 whitespace-nowrap">dicht</span> : minutenMerk(route.minutenStand?.ophalen)?.merk}
          />
          <MetriekRij
            label="Afleveren"
            waarde={String(route.stopCountAfleveren || 0)}
            totaal={route.capaciteitTotaalAfleveren != null ? String(route.capaciteitTotaalAfleveren) : null}
            pct={metriekPercentage(route.stopCountAfleveren || 0, route.capaciteitTotaalAfleveren)}
            kleur={metriekKleur(route.stopCountAfleveren || 0, route.capaciteitTotaalAfleveren, true)}
            overschreden={route.capaciteitResterendAfleveren != null && route.capaciteitResterendAfleveren < 0}
            title={route.afleverenNietVoorstellen ? 'Afleveren staat dicht voor nieuwe voorstellen' : minutenMerk(route.minutenStand?.afleveren)?.title}
            merk={route.afleverenNietVoorstellen ? <span className="text-[9px] text-gray-500 whitespace-nowrap">dicht</span> : minutenMerk(route.minutenStand?.afleveren)?.merk}
          />
          <RouteDuur route={route} variant="card" stil={isInProgress} />
        </div>
      )}
    </div>
  );
}

// Compact route card for month view - shows route name, stop count & capacity color coding
function MonthRouteCard({
  route,
  onClick,
  onMergeClick,
  onAssignChauffeur,
  onCancelClick,
  onRescheduleClick,
}: {
  route: RouteListItem;
  onClick: (e: React.MouseEvent) => void;
  onMergeClick: (route: RouteListItem) => void;
  onAssignChauffeur?: (route: RouteListItem) => void;
  onCancelClick?: (route: RouteListItem) => void;
  onRescheduleClick?: (route: RouteListItem) => void;
}) {
  const displayName = route.routeName || route.name;
  const isCompleted = route.status === 'Afgerond';
  const isInProgress = route.status === 'Onderweg';


  const getBorderColor = () => {
    if (isCompleted) return 'border-l-emerald-500';
    if (isInProgress) return 'border-l-amber-500';
    return 'border-l-gray-200';
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick(e as unknown as React.MouseEvent);
        }
      }}
      className={clsx(
        'w-full text-left rounded border border-gray-200 bg-white transition-colors cursor-pointer',
        'hover:bg-gray-50 hover:border-gray-300',
        'focus:outline-none focus:ring-1 focus:ring-indigo-500',
        'border-l-[3px]',
        getBorderColor(),
        isCompleted && 'opacity-50'
      )}
    >
      {/* Route name + stop count + new-stops badge */}
      <div className="flex items-center justify-between gap-1 px-1.5 py-1">
        <span className="text-[11px] font-medium text-gray-900 truncate leading-tight">
          {displayName}
        </span>
        <div className="flex items-center gap-1 flex-shrink-0">
          {route.locked && route.stopsAddedAfterLock > 0 && (
            <span className="inline-flex items-center gap-0.5 bg-amber-100 text-amber-700 text-[9px] font-bold px-1 py-0.5 rounded-full animate-pulse" title={`${route.stopsAddedAfterLock} stop(s) toegevoegd na vergrendeling`}>
              <PlusCircleIcon className="w-2.5 h-2.5" />
              +{route.stopsAddedAfterLock}
            </span>
          )}
          <span className="text-[10px] font-semibold text-gray-600 bg-gray-100 px-1.5 rounded-full leading-tight tabular-nums" title={`${route.stopCount} stops`}>
            {route.stopCount}
          </span>
          <RouteCardMenu
            route={route}
            compact
            onOpenInPlanner={() =>
              window.open(`/route-planner?routeId=${route.id}`, '_blank')
            }
            onMergeClick={() => onMergeClick(route)}
            onAssignChauffeur={onAssignChauffeur ? () => onAssignChauffeur(route) : undefined}
            onCancelClick={onCancelClick ? () => onCancelClick(route) : undefined}
            onRescheduleClick={onRescheduleClick ? () => onRescheduleClick(route) : undefined}
          />
        </div>
      </div>

      {/* Compact capacity indicators + duur */}
      {!isCompleted && (
        <div className="flex items-center gap-1 px-1.5 pb-1">
          <RouteDuur route={route} variant="compact" stil={isInProgress} />
          <CapacityCompact label="↑" count={route.stopCountOphalen || 0} total={route.capaciteitTotaalOphalen} />
          <CapacityCompact label="↓" count={route.stopCountAfleveren || 0} total={route.capaciteitTotaalAfleveren} />
        </div>
      )}
    </div>
  );
}

export function CalendarWeekView() {
  const { currentDate, routesByDate, isLoading, selectedDate, setSelectedDate } = useCalendarStore();
  const [createRouteDate, setCreateRouteDate] = useState<string | null>(null);
  const [mergeSourceRoute, setMergeSourceRoute] = useState<RouteListItem | null>(null);
  const [chauffeurRoute, setChauffeurRoute] = useState<RouteListItem | null>(null);
  const [cancelRoute, setCancelRoute] = useState<RouteListItem | null>(null);
  const [rescheduleRoute, setRescheduleRoute] = useState<RouteListItem | null>(null);

  // Generate week days
  const weekDays = useMemo(() => {
    const start = startOfWeek(currentDate, { locale: nl });
    return Array.from({ length: 7 }, (_, i) => addDays(start, i));
  }, [currentDate]);

  const handleRouteClick = (routeId: string) => {
    window.open(`/route-planner?routeId=${routeId}`, '_blank');
  };

  const handleDayClick = (date: Date) => {
    setSelectedDate(isSameDay(date, selectedDate || new Date(0)) ? null : date);
  };

  return (
    <div className="flex-1 overflow-auto">
      {/* Day headers */}
      <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50 sticky top-0 z-10">
        {weekDays.map((day) => (
            <div
              key={day.toISOString()}
              className={clsx(
                'p-4 text-center border-r border-gray-200 last:border-r-0',
                isToday(day) && 'bg-accent-50'
              )}
            >
              <p className="text-xs font-medium text-muted uppercase">
                {format(day, 'EEE', { locale: nl })}
              </p>
              <p
                className={clsx(
                  'text-2xl font-heading mt-1',
                  isToday(day)
                    ? 'text-accent font-bold'
                    : 'text-main'
                )}
              >
                {format(day, 'd')}
              </p>
              <button
                onClick={(e) => { e.stopPropagation(); setCreateRouteDate(format(day, 'yyyy-MM-dd')); }}
                className="absolute top-2 right-2 text-gray-300 hover:text-indigo-600 transition-colors"
                title="Route toevoegen"
              >
                <PlusCircleIcon className="w-5 h-5" />
              </button>
            </div>
        ))}
      </div>

      {/* Day columns with routes */}
      <div className="grid grid-cols-7 min-h-[500px]">
        {weekDays.map((day) => {
          const dateKey = format(day, 'yyyy-MM-dd');
          const dayRoutes = routesByDate[dateKey] || [];
          const dubbel = dubbeleChauffeurs(dayRoutes);

          return (
            <div
              key={day.toISOString()}
              onClick={() => handleDayClick(day)}
              className={clsx(
                'border-r border-gray-200 last:border-r-0 p-2 min-h-full cursor-pointer transition-colors group/cell',
                isToday(day) && 'bg-calendar-today/30',
                selectedDate && isSameDay(day, selectedDate) && 'bg-accent-50',
                !isToday(day) && !selectedDate && 'hover:bg-gray-50'
              )}
            >
              {isLoading ? (
                <div className="flex items-center justify-center h-24">
                  <div className="w-5 h-5 border-2 border-accent border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (
                <>
                  {dayRoutes.length === 0 ? (
                    <p className="text-xs text-muted text-center py-4">
                      Geen routes
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {dayRoutes.map((route) => (
                        <RouteCard
                          key={route.id}
                          route={route}
                          onClick={() => handleRouteClick(route.id)}
                          onMergeClick={setMergeSourceRoute}
                          onAssignChauffeur={setChauffeurRoute}
                          onCancelClick={setCancelRoute}
                          onRescheduleClick={setRescheduleRoute}
                          dubbel={!!route.chauffeurId && dubbel.has(route.chauffeurId)}
                        />
                      ))}
                    </div>
                  )}
                  <button
                    onClick={(e) => { e.stopPropagation(); setCreateRouteDate(dateKey); }}
                    className="w-full mt-2 py-2 flex items-center justify-center gap-1.5 text-xs text-gray-400 hover:text-indigo-600 border border-dashed border-gray-200 hover:border-indigo-300 rounded-lg transition-colors"
                    title="Route toevoegen"
                  >
                    <PlusCircleIcon className="w-4 h-4" />
                    <span>Route toevoegen</span>
                  </button>
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* Create Route Modal */}
      {createRouteDate && (
        <CreateRouteModal
          datum={createRouteDate}
          onClose={() => setCreateRouteDate(null)}
        />
      )}

      {/* Merge Routes Modal */}
      {mergeSourceRoute && (
        <MergeRoutesModal
          sourceRoute={mergeSourceRoute}
          onClose={() => setMergeSourceRoute(null)}
        />
      )}

      {chauffeurRoute && (
        <ChauffeurWisselDialog route={chauffeurRoute} onClose={() => setChauffeurRoute(null)} />
      )}

      {cancelRoute && (
        <CancelRouteModal route={cancelRoute} onClose={() => setCancelRoute(null)} />
      )}

      {rescheduleRoute && (
        <RescheduleRouteModal
          routeId={rescheduleRoute.id}
          routeName={rescheduleRoute.routeName || rescheduleRoute.name}
          oldDate={rescheduleRoute.datum}
          chauffeurName={rescheduleRoute.chauffeurName}
          ophalenCount={rescheduleRoute.stopCountOphalen || 0}
          terugbrengenCount={rescheduleRoute.stopCountAfleveren || 0}
          locked={rescheduleRoute.locked}
          onClose={() => setRescheduleRoute(null)}
        />
      )}
    </div>
  );
}

export function CalendarMonthView() {
  const { currentDate, routesByDate, isLoading } = useCalendarStore();
  const [mergeSourceRoute, setMergeSourceRoute] = useState<RouteListItem | null>(null);
  const [chauffeurRoute, setChauffeurRoute] = useState<RouteListItem | null>(null);
  const [cancelRoute, setCancelRoute] = useState<RouteListItem | null>(null);
  const [rescheduleRoute, setRescheduleRoute] = useState<RouteListItem | null>(null);

  // Generate all days for the month view (including padding days)
  const monthDays = useMemo(() => {
    const start = startOfWeek(new Date(currentDate.getFullYear(), currentDate.getMonth(), 1), { locale: nl });
    const days: Date[] = [];

    for (let i = 0; i < 42; i++) { // 6 weeks
      days.push(addDays(start, i));
    }

    return days;
  }, [currentDate]);

  const handleRouteClick = (routeId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    window.open(`/route-planner?routeId=${routeId}`, '_blank');
  };

  return (
    <div className="flex-1 overflow-auto">
      {/* Day headers */}
      <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50 sticky top-0 z-10">
        {['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo'].map((day) => (
          <div key={day} className="p-2 text-center border-r border-gray-200 last:border-r-0">
            <p className="text-xs font-medium text-muted uppercase">{day}</p>
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 auto-rows-fr">
        {monthDays.map((day) => {
          const dateKey = format(day, 'yyyy-MM-dd');
          const dayRoutes = routesByDate[dateKey] || [];
          const isCurrentMonth = day.getMonth() === currentDate.getMonth();

          return (
            <div
              key={day.toISOString()}
              className={clsx(
                'border-r border-b border-gray-200 p-1 min-h-[120px] transition-colors',
                isToday(day) && 'bg-calendar-today/30',
                !isCurrentMonth && 'bg-gray-50 opacity-50'
              )}
            >
              <p
                className={clsx(
                  'text-sm font-medium p-1',
                  isToday(day)
                    ? 'bg-accent text-white rounded-full w-7 h-7 flex items-center justify-center'
                    : 'text-main'
                )}
              >
                {format(day, 'd')}
              </p>

              {isLoading ? null : (
                <div className="space-y-1 mt-1">
                  {dayRoutes.slice(0, 4).map((route) => (
                    <MonthRouteCard
                      key={route.id}
                      route={route}
                      onClick={(e) => handleRouteClick(route.id, e)}
                      onMergeClick={setMergeSourceRoute}
                      onAssignChauffeur={setChauffeurRoute}
                      onCancelClick={setCancelRoute}
                      onRescheduleClick={setRescheduleRoute}
                    />
                  ))}
                  {dayRoutes.length > 4 && (
                    <p className="text-xs text-muted px-1">
                      +{dayRoutes.length - 4} meer
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Merge Routes Modal */}
      {mergeSourceRoute && (
        <MergeRoutesModal
          sourceRoute={mergeSourceRoute}
          onClose={() => setMergeSourceRoute(null)}
        />
      )}

      {chauffeurRoute && (
        <ChauffeurWisselDialog route={chauffeurRoute} onClose={() => setChauffeurRoute(null)} />
      )}

      {cancelRoute && (
        <CancelRouteModal route={cancelRoute} onClose={() => setCancelRoute(null)} />
      )}

      {rescheduleRoute && (
        <RescheduleRouteModal
          routeId={rescheduleRoute.id}
          routeName={rescheduleRoute.routeName || rescheduleRoute.name}
          oldDate={rescheduleRoute.datum}
          chauffeurName={rescheduleRoute.chauffeurName}
          ophalenCount={rescheduleRoute.stopCountOphalen || 0}
          terugbrengenCount={rescheduleRoute.stopCountAfleveren || 0}
          locked={rescheduleRoute.locked}
          onClose={() => setRescheduleRoute(null)}
        />
      )}
    </div>
  );
}

export function CalendarDayView() {
  const { currentDate, routesByDate, isLoading, chauffeurs } = useCalendarStore();
  const [mergeSourceRoute, setMergeSourceRoute] = useState<RouteListItem | null>(null);
  const [chauffeurRoute, setChauffeurRoute] = useState<RouteListItem | null>(null);
  const [cancelRoute, setCancelRoute] = useState<RouteListItem | null>(null);
  const [rescheduleRoute, setRescheduleRoute] = useState<RouteListItem | null>(null);

  const dateKey = format(currentDate, 'yyyy-MM-dd');
  const dayRoutes = routesByDate[dateKey] || [];
  const dubbel = dubbeleChauffeurs(dayRoutes);

  // Group routes by chauffeur for swimlane view
  const routesByChaffeur = useMemo(() => {
    const grouped: Record<string, RouteListItem[]> = {
      'unassigned': [],
    };

    // Initialize with all chauffeurs
    chauffeurs.forEach(c => {
      grouped[c.id] = [];
    });

    dayRoutes.forEach(route => {
      const key = route.chauffeurId || 'unassigned';
      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key]!.push(route);
    });

    return grouped;
  }, [dayRoutes, chauffeurs]);

  const handleRouteClick = (routeId: string) => {
    window.open(`/route-planner?routeId=${routeId}`, '_blank');
  };

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto p-6">
      <div className="space-y-4">
        {/* Unassigned routes */}
        {(routesByChaffeur['unassigned']?.length ?? 0) > 0 && (
          <div className="bg-gray-100 rounded-card p-4">
            <h3 className="text-sm font-medium text-muted mb-3">
              Niet toegewezen
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {routesByChaffeur['unassigned']?.map((route) => (
                <RouteCard
                  key={route.id}
                  route={route}
                  onClick={() => handleRouteClick(route.id)}
                  onMergeClick={setMergeSourceRoute}
                  onAssignChauffeur={setChauffeurRoute}
                  onCancelClick={setCancelRoute}
                  onRescheduleClick={setRescheduleRoute}
                />
              ))}
            </div>
          </div>
        )}

        {/* Chauffeur swimlanes */}
        {chauffeurs.map((chauffeur) => {
          const routes = routesByChaffeur[chauffeur.id] || [];

          return (
            <div key={chauffeur.id} className="bg-white rounded-card p-4 shadow-sm">
              <div className="flex items-center gap-3 mb-3">
                <Avatar name={chauffeur.name} size="md" />
                <div>
                  <h3 className="font-medium text-main">
                    {chauffeur.name}
                  </h3>
                  <p className="text-xs text-muted">
                    {routes.length} {routes.length === 1 ? 'route' : 'routes'}
                  </p>
                </div>
              </div>

              {routes.length === 0 ? (
                <p className="text-sm text-muted py-4 text-center">
                  Geen routes gepland
                </p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {routes.map((route) => (
                    <RouteCard
                      key={route.id}
                      route={route}
                      onClick={() => handleRouteClick(route.id)}
                      onMergeClick={setMergeSourceRoute}
                      onAssignChauffeur={setChauffeurRoute}
                      onCancelClick={setCancelRoute}
                      onRescheduleClick={setRescheduleRoute}
                      dubbel={dubbel.has(chauffeur.id)}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Merge Routes Modal */}
      {mergeSourceRoute && (
        <MergeRoutesModal
          sourceRoute={mergeSourceRoute}
          onClose={() => setMergeSourceRoute(null)}
        />
      )}

      {chauffeurRoute && (
        <ChauffeurWisselDialog route={chauffeurRoute} onClose={() => setChauffeurRoute(null)} />
      )}

      {cancelRoute && (
        <CancelRouteModal route={cancelRoute} onClose={() => setCancelRoute(null)} />
      )}

      {rescheduleRoute && (
        <RescheduleRouteModal
          routeId={rescheduleRoute.id}
          routeName={rescheduleRoute.routeName || rescheduleRoute.name}
          oldDate={rescheduleRoute.datum}
          chauffeurName={rescheduleRoute.chauffeurName}
          ophalenCount={rescheduleRoute.stopCountOphalen || 0}
          terugbrengenCount={rescheduleRoute.stopCountAfleveren || 0}
          locked={rescheduleRoute.locked}
          onClose={() => setRescheduleRoute(null)}
        />
      )}
    </div>
  );
}
